import { ActionSource } from 'owa-analytics-actions';
import { OwaDate } from 'owa-datetime';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FilterableMenuItem, ShouldShowBehavior } from 'owa-filterable-menu';
import { showInImmersiveReader } from 'owa-immersive-reader-store';
import { getCategoryMenuPropertiesForContextMenu } from 'owa-mail-category-view';
import createScheduleSubMenu from 'owa-mail-datepicker/lib/utils/createScheduleSubMenu';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { lazyMarkAsReadInTable } from 'owa-mail-mark-read-actions';
import { getCopyToProperties, getMoveToPropertiesForContextMenu } from 'owa-mail-movetofolder-view';
import { lazyMoveReadingPaneToTab } from 'owa-mail-reading-pane-store';
import isReadingPaneBottom from 'owa-mail-reading-pane-store/lib/utils/isReadingPaneBottom';
import { lazyOnReportAbuseOptionSelected } from 'owa-mail-report-abuse';
import { getAssignPolicyPropertiesForMailContextMenu } from 'owa-mail-retention-policies';
import { lazyShowInboxRuleDialog } from 'owa-mail-rule-view';
import { lazyScheduleItems } from 'owa-mail-triage-action';
import * as lazyTriageActions from 'owa-mail-triage-action';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import strings from 'owa-strings/owa-mail-strings';
import {
    listViewStore,
    MailRowDataPropertyGetter,
    TableView,
    TableQueryType,
    shouldSuppressServerMarkReadOnReplyOrForward,
} from 'owa-mail-list-store';
import {
    getMailMenuItemShouldShowMap,
    getMailMenuItemShouldDisable,
    MenuItemType,
} from 'owa-mail-filterable-menu-behavior';
import {
    lazyForwardConversation,
    lazyReplyToConversation,
    lazyForwardItem,
    lazyReplyToItem,
    lazyShowConversationInImmersiveReader,
} from 'owa-mail-message-actions';

const contextMenuActionSource = 'ContextMenu';

// Used as a key for divider, because IContextualMenuItem requires each element to have a different key
let dividerKey = 0;

/**
 * Get an array of filterable menu items for mail list context menu
 * @param rowKeys the row keys to show the context menu
 * @param tableViewId the id of the table that contains these rows
 * @return the context menu items
 */
export default function getFilterableMailListContextMenuItems(
    rowKeys: string[],
    tableViewId: string,
    onDismiss: (ev?: any) => void
): FilterableMenuItem[] {
    const shouldShowBehaviorMap = getMailMenuItemShouldShowMap();

    return [
        // Reply/Reply all/Forward
        new FilterableMenuItem(
            strings.itemHeaderReply,
            null /* icon */,
            () => onReplyRow(rowKeys[0], tableViewId),
            shouldShowBehaviorMap[MenuItemType.Reply]
        ),
        new FilterableMenuItem(
            strings.itemHeaderReplyAll,
            null /* icon */,
            () => onReplyAllRow(rowKeys[0], tableViewId),
            shouldShowBehaviorMap[MenuItemType.ReplyAllForward]
        ),
        new FilterableMenuItem(
            strings.itemHeaderForward,
            null /* icon */,
            () => onForwardRow(rowKeys[0], tableViewId),
            shouldShowBehaviorMap[MenuItemType.ReplyAllForward]
        ),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.Move]() ||
                shouldShowBehaviorMap[MenuItemType.Copy]() ||
                shouldShowBehaviorMap[MenuItemType.Delete]() ||
                shouldShowBehaviorMap[MenuItemType.Restore]() ||
                shouldShowBehaviorMap[MenuItemType.Archive]() ||
                shouldShowBehaviorMap[MenuItemType.EmptyFolder]() ||
                shouldShowBehaviorMap[MenuItemType.Categories]()
            );
        }),

        // Delete/Restore/Archive
        new FilterableMenuItem(
            strings.deleteItem,
            null /* icon */,
            () => onDeleteClicked(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Delete],
            null /* display */,
            getMailMenuItemShouldDisable(MenuItemType.Delete)
        ),
        new FilterableMenuItem(
            strings.restoreItem,
            null /* icon */,
            () =>
                lazyTriageActions.lazyRestoreMailListRowsAction.importAndExecute(
                    rowKeys,
                    tableViewId
                ),
            shouldShowBehaviorMap[MenuItemType.Restore]
        ),
        new FilterableMenuItem(
            strings.archive,
            null /* icon */,
            () => onArchiveClicked(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Archive]
        ),
        new FilterableMenuItem(
            strings.move,
            null, // no - icon
            () => {},
            shouldShowBehaviorMap[MenuItemType.Move],
            null /* displayOption*/,
            false /* disabled */,
            getMoveToPropertiesForContextMenu(
                tableViewId,
                onDismiss,
                contextMenuActionSource,
                shouldShowBehaviorMap
            )
        ),
        new FilterableMenuItem(
            strings.copy,
            null, // no - icon
            () => {},
            shouldShowBehaviorMap[MenuItemType.Copy],
            null /* displayOption*/,
            false /* disabled */,
            getCopyToProperties(tableViewId, onDismiss, contextMenuActionSource)
        ),
        new FilterableMenuItem(
            strings.categoryMenuItemMenuText,
            null, // no - icon
            () => {},
            shouldShowBehaviorMap[MenuItemType.Categories],
            null /* displayOption*/,
            false /* disabled */,
            getCategoryMenuPropertiesForContextMenu(tableViewId, onDismiss, 'ContextLV')
        ),

        // Empty folder
        new FilterableMenuItem(
            strings.deleteItem,
            null /* icon */,
            () => {
                const tableView = listViewStore.tableViews.get(tableViewId);

                lazyTriageActions.lazyEmptyTableView.importAndExecute(
                    tableView,
                    false /*isExplicitSoftDelete*/,
                    'ContextLV',
                    tableView.virutalSelectAllExclusionList
                );
            },
            shouldShowBehaviorMap[MenuItemType.EmptyFolder]
        ),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.MarkAsRead]() ||
                shouldShowBehaviorMap[MenuItemType.MarkAsUnread]() ||
                shouldShowBehaviorMap[MenuItemType.Flag]() ||
                shouldShowBehaviorMap[MenuItemType.Unflag]() ||
                shouldShowBehaviorMap[MenuItemType.Pin]() ||
                shouldShowBehaviorMap[MenuItemType.Unpin]()
            );
        }),

        // Mark as read/unread
        new FilterableMenuItem(
            strings.markAsRead,
            null /* icon */,
            () => onMarkAsReadUnreadClicked(tableViewId, true /* isReadValueSet */),
            shouldShowBehaviorMap[MenuItemType.MarkAsRead]
        ),
        new FilterableMenuItem(
            strings.markAsUnread,
            null /* icon */,
            () => onMarkAsReadUnreadClicked(tableViewId, false /* isReadValueSet */),
            shouldShowBehaviorMap[MenuItemType.MarkAsUnread]
        ),

        // Schedule/Unschedule
        new FilterableMenuItem(
            isFeatureEnabled('tri-snooze-exp') ? strings.snooze : strings.schedule,
            null, // no - icon
            () => {},
            shouldShowBehaviorMap[MenuItemType.Schedule],
            null /* displayOption*/,
            false /* disabled */,
            createScheduleSubMenu(
                tableViewId,
                contextMenuActionSource /* actionSource */,
                onScheduleItemClicked
            )
        ),

        // Flag/Unflag
        new FilterableMenuItem(
            strings.flag,
            null /* icon */,
            () => markRowsAsFlagged(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Flag]
        ),
        new FilterableMenuItem(
            strings.unflag,
            null /* icon */,
            () => markRowsAsNotFlagged(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Unflag]
        ),

        // Pin/unpin
        new FilterableMenuItem(
            strings.pin,
            null /* icon */,
            () => markRowsPinnedUnpinned(rowKeys, tableViewId, true /* shouldMarkAsPinned */),
            shouldShowBehaviorMap[MenuItemType.Pin]
        ),
        new FilterableMenuItem(
            strings.unpin,
            null /* icon */,
            () => markRowsPinnedUnpinned(rowKeys, tableViewId, false /* shouldMarkAsPinned */),
            shouldShowBehaviorMap[MenuItemType.Unpin]
        ),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.MarkAsJunk]() ||
                shouldShowBehaviorMap[MenuItemType.MarkAsNotJunk]() ||
                shouldShowBehaviorMap[MenuItemType.Ignore]() ||
                shouldShowBehaviorMap[MenuItemType.StopIgnoring]() ||
                shouldShowBehaviorMap[MenuItemType.Block]()
            );
        }),

        // Junk/Not junk/Block
        new FilterableMenuItem(
            strings.markAsJunk,
            null /* icon */,
            () => onJunkNotJunkClicked(rowKeys, tableViewId, true /* isJunkValueToSet */),
            shouldShowBehaviorMap[MenuItemType.MarkAsJunk]
        ),
        new FilterableMenuItem(
            strings.markAsNotJunk,
            null /* icon */,
            () => onJunkNotJunkClicked(rowKeys, tableViewId, false /* isJunkValueToSet */),
            shouldShowBehaviorMap[MenuItemType.MarkAsNotJunk]
        ),
        new FilterableMenuItem(
            strings.ignore,
            null /* icon */,
            () => onIgnoreStopIgnoreClicked(rowKeys, tableViewId, true),
            shouldShowBehaviorMap[MenuItemType.Ignore]
        ),
        new FilterableMenuItem(
            strings.reportAbuse,
            null /* icon */,
            () => onReportAbuseClicked(rowKeys[0], tableViewId),
            shouldShowBehaviorMap[MenuItemType.ReportAbuse]
        ),
        new FilterableMenuItem(
            strings.stopIgnoring,
            null /* icon */,
            () => onIgnoreStopIgnoreClicked(rowKeys, tableViewId, false),
            shouldShowBehaviorMap[MenuItemType.StopIgnoring]
        ),
        new FilterableMenuItem(
            strings.block,
            null /* icon */,
            () => onBlockClicked(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Block]
        ),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.InboxRule]() ||
                shouldShowBehaviorMap[MenuItemType.Translate]() ||
                shouldShowBehaviorMap[MenuItemType.ShowInImmersiveReader]()
            );
        }),

        new FilterableMenuItem(
            strings.createRule,
            null, // no - icon
            () => onCreateRuleClicked(rowKeys[0], tableViewId),
            shouldShowBehaviorMap[MenuItemType.InboxRule],
            null /* displayOption */
        ),

        // Translate
        new FilterableMenuItem(
            strings.itemHeaderTranslate,
            null, // no - icon
            () => onTranslateClicked(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.Translate]
        ),

        // Show in immersive reader
        new FilterableMenuItem(
            strings.showInImmersiveReader,
            null, // no - icon
            () => {
                onShowInImmersiveReaderClicked(rowKeys[0], tableViewId);
            },
            shouldShowBehaviorMap[MenuItemType.ShowInImmersiveReader]
        ),

        new FilterableMenuItem(
            strings.openInNewTab,
            null, // no - icon
            () => onOpenInNewTabClicked(rowKeys, tableViewId),
            shouldShowBehaviorMap[MenuItemType.OpenInNewTab]
        ),

        // Assign Policy
        new FilterableMenuItem(
            strings.assignPolicyMenuItemName,
            null, // no - icon
            () => {},
            shouldShowBehaviorMap[MenuItemType.AssignPolicy],
            null /* displayOption*/,
            false /* disabled */,
            getAssignPolicyPropertiesForMailContextMenu(tableViewId, 'ContextLV')
        ),
    ];
}

/**
 * Called when user clicks on schedule
 * @param tableView the table view
 * @param actionSource action source of action
 * @param date date to schedule to
 */
function onScheduleItemClicked(tableView: TableView, actionSource: ActionSource, date?: OwaDate) {
    const rowKeys = [...tableView.selectedRowKeys.keys()];

    let isScheduled = false;

    // In this case we don't need to know if all items are being rescheduled, just that one of the N
    // selected was previously scheduled before.
    for (let i = 0; i < rowKeys.length; i++) {
        const snoozed = MailRowDataPropertyGetter.getIsSnoozed(rowKeys[i], tableView);
        if (snoozed) {
            isScheduled = true;
            break;
        }
    }
    lazyScheduleItems.importAndExecute(rowKeys, tableView, actionSource, date, isScheduled);
}

/**
 * Called when user clicks on the reply menu item
 * @param rowKey the row key
 * @param tableViewId the table view id
 */
function onReplyRow(rowKey: string, tableViewId: string) {
    onReplyReplyAllInternal(false /* isReplyAll */, rowKey, tableViewId);
}

/**
 * Called when user clicks on the reply all menu item
 * @param rowKey the row key
 * @param tableViewId the table view id
 */
function onReplyAllRow(rowKey: string, tableViewId: string) {
    onReplyReplyAllInternal(true /* isReplyAll */, rowKey, tableViewId);
}

let onReplyReplyAllInternal = (isReplyAll: boolean, rowKey: string, tableViewId: string) => {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const suppressServerMarkReadOnReplyOrForward = shouldSuppressServerMarkReadOnReplyOrForward(
        tableView
    );
    const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKey, tableView);
    let instrumentationContexts;
    if (tableView.tableQuery.type == TableQueryType.Search) {
        instrumentationContexts = getInstrumentationContextsFromTableView([rowKey], tableView);
    }
    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            lazyReplyToConversation.importAndExecute(
                rowId,
                isReplyAll,
                'ContextMenu',
                instrumentationContexts,
                isReadingPaneBottom() /*isFullCompose*/,
                suppressServerMarkReadOnReplyOrForward
            );
            break;

        case ReactListViewType.Message:
            lazyReplyToItem.importAndExecute(
                rowId,
                isReplyAll,
                instrumentationContexts,
                'ContextMenu',
                suppressServerMarkReadOnReplyOrForward
            );
    }
};

/**
 * Called when user clicks on the forward menu item
 * @param rowKey the row key
 * @param tableViewId the table view id
 */
function onForwardRow(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKey, tableView);
    const suppressServerMarkReadOnReplyOrForward = shouldSuppressServerMarkReadOnReplyOrForward(
        tableView
    );
    let instrumentationContexts;
    if (tableView.tableQuery.type == TableQueryType.Search) {
        instrumentationContexts = getInstrumentationContextsFromTableView([rowKey], tableView);
    }
    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            lazyForwardConversation.importAndExecute(
                rowId,
                'ContextMenu',
                instrumentationContexts,
                suppressServerMarkReadOnReplyOrForward
            );
            break;
        case ReactListViewType.Message:
            lazyForwardItem.importAndExecute(
                rowId,
                'ContextMenu',
                instrumentationContexts,
                suppressServerMarkReadOnReplyOrForward
            );
            break;
    }
}

/**
 * Create a divider which is used in the mail list context menu
 * @param shouldShow the should show behavior which indicates whether to show the context menu or not
 */
function newDivider(shouldShowBehavior?: ShouldShowBehavior): FilterableMenuItem {
    return new FilterableMenuItem(
        '-' /* name */,
        null /* icon */,
        null /* onClick */,
        shouldShowBehavior,
        null /* displayOption */,
        null /* disabled*/,
        null /* menuContent */,
        (dividerKey++).toString()
    );
}

/**
 * Called when user clicks on the delete menu item
 */
function onDeleteClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    if (tableView.isInVirtualSelectAllMode) {
        // When in virtual selection, i.e non search and select all mode,
        // delete all in the folder
        lazyTriageActions.lazyEmptyTableView.importAndExecute(
            tableView,
            false /*isExplicitSoftDelete*/,
            'ContextLV',
            tableView.virutalSelectAllExclusionList
        );
    } else {
        lazyTriageActions.lazyDeleteMailListRows.importAndExecute(
            rowKeys,
            tableViewId,
            false /*isExplicitSoftDelete*/,
            contextMenuActionSource
        );
    }
}

/**
 * Called when user clicks on the archive menu item
 */
function onArchiveClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyTriageActions.lazyArchiveMailListRows.importAndExecute(
        rowKeys,
        tableView,
        contextMenuActionSource
    );
}

/**
 * Called when the user clicks on the "Show in immersive reader" menu item
 */
function onShowInImmersiveReaderClicked(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKey, tableView);

    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            lazyShowConversationInImmersiveReader.importAndExecute(
                rowId /* conversationId */,
                contextMenuActionSource
            );
            break;

        case ReactListViewType.Message:
            showInImmersiveReader(rowId /* itemId */, contextMenuActionSource);
            break;
    }
}

/**
 * Called when the user clicks on the "Open in new tab" menu item
 */
function onOpenInNewTabClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const instrumentationContexts = getInstrumentationContextsFromTableView(rowKeys, tableView);
    for (let i = 0; i < rowKeys.length; i++) {
        const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKeys[i], tableView);
        const rowSubject = MailRowDataPropertyGetter.getSubject(rowKeys[i], tableView);
        const rowCategories = MailRowDataPropertyGetter.getCategories(rowKeys[i], tableView);
        lazyMoveReadingPaneToTab.importAndExecute(
            rowId,
            rowSubject,
            rowCategories,
            true /*makeActive*/,
            instrumentationContexts[i]
        );
    }
}

function onMarkAsReadUnreadClicked(tableViewId: string, isReadValueSet: boolean) {
    const tableView = listViewStore.tableViews.get(tableViewId);

    lazyMarkAsReadInTable.importAndExecute(
        contextMenuActionSource,
        tableView.virutalSelectAllExclusionList /* exclusionList */,
        tableView.isInVirtualSelectAllMode /* isActingOnAllItemsInTable */,
        isReadValueSet,
        [...tableView.selectedRowKeys.keys()],
        tableView
    );
}

function onBlockClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyTriageActions.lazyBlockLastSendersFromTable.importAndExecute(
        rowKeys,
        tableView,
        'ContextLV'
    );
}

function markRowsAsFlagged(rowKeys: string[], tableViewId: string) {
    lazyTriageActions.lazySetMailListRowsFlagState.importAndExecute(
        rowKeys,
        tableViewId,
        { FlagStatus: 'Flagged' },
        contextMenuActionSource
    );
}

function markRowsAsNotFlagged(rowKeys: string[], tableViewId: string) {
    lazyTriageActions.lazySetMailListRowsFlagState.importAndExecute(
        rowKeys,
        tableViewId,
        { FlagStatus: 'NotFlagged' },
        contextMenuActionSource
    );
}

function onJunkNotJunkClicked(rowKeys: string[], tableViewId: string, isJunkValueToSet: boolean) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyTriageActions.lazyMarkRowsJunkNotJunk.importAndExecute(
        rowKeys,
        tableView,
        isJunkValueToSet,
        contextMenuActionSource /* actionSource */
    );
}

async function onIgnoreStopIgnoreClicked(
    rowKeys: string[],
    tableViewId: string,
    shouldIgnore: boolean
) {
    const toggleIgnoreConversations = await lazyTriageActions.lazyToggleIgnoreConversations.import();
    toggleIgnoreConversations(rowKeys, tableViewId, shouldIgnore, 'ContextLV');
}

function markRowsPinnedUnpinned(
    rowKeys: string[],
    tableViewId: string,
    shouldMarkAsPinned: boolean
) {
    lazyTriageActions.lazyMarkRowsPinnedUnpinned.importAndExecute(
        rowKeys,
        tableViewId,
        shouldMarkAsPinned,
        contextMenuActionSource
    );
}

/**
 * Called when user clicks on the create rule menu item
 */
function onCreateRuleClicked(rowKey: string, tableViewId: string) {
    // Get the sender from the single selected conversation.
    // Ensuring a single selection and that the selection
    // has an SMTP address is handled in the context menu should show behavior map
    const sender = MailRowDataPropertyGetter.getLastSenderMailbox(
        rowKey,
        listViewStore.tableViews.get(tableViewId)
    );
    lazyShowInboxRuleDialog.importAndExecute(sender, 'ContextLV');
}

/**
 * Called when the user clicks on the "Translate" menu item
 */
function onTranslateClicked(rowKeys: string[], tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    lazyTriageActions.lazyTranslateFromTable.importAndExecute(rowKeys, tableView, 'ContextLV');
}

/**
 * Called when the user clicks on the "Report Abuse" menu item
 */
function onReportAbuseClicked(rowKey: string, tableViewId: string) {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const instrumentationContexts = getInstrumentationContextsFromTableView([rowKey], tableView);
    lazyOnReportAbuseOptionSelected.importAndExecute(
        rowKey,
        tableView,
        instrumentationContexts,
        contextMenuActionSource
    );
}
